<template>
  <main class="collections-agent">
    <section class="collections-header">
      <div class="collections-title">
        <div class="agent-icon">
          <v-icon color="white" size="30">mdi-phone-outline</v-icon>
        </div>
        <div>
          <h1>Agente de Cobranza</h1>
          <p>
            Gestiona cartera, facturas, vencimientos y conversaciones desde una
            vista operativa preparada para cobranza preventiva y vencida.
          </p>
        </div>
      </div>

      <div class="header-actions">
        <div class="sync-control">
          <v-btn
            outlined
            class="details-button"
            :loading="syncingDebtors"
            :disabled="syncingDebtors"
            @click="syncDebtors"
          >
            <v-icon left size="18">mdi-sync</v-icon>
            Sincronizar
          </v-btn>
          <small v-if="lastSyncLabel" class="sync-hint">
            Sincronizado {{ lastSyncLabel }}
          </small>
          <small v-else class="sync-hint">Sin sincronizar aún</small>
        </div>
        <v-btn outlined class="details-button" @click="openCreateDebtor">
          <v-icon left size="18">mdi-account-plus-outline</v-icon>
          Crear deudor
        </v-btn>
        <v-btn class="primary-action" elevation="0" @click="uploadDialog = true">
          <v-icon left size="20">mdi-upload-outline</v-icon>
          Actualizar cartera
        </v-btn>
      </div>
    </section>

    <section class="insight-banner">
      <div>
        <span>Cartera viva</span>
        <strong>
          Sube estados de cuenta para detectar facturas nuevas, pagos aplicados,
          cambios de estatus y próximos vencimientos.
        </strong>
      </div>
      <button type="button" @click="activeTab = 'calendar'">
        Ver calendario
        <v-icon size="18">mdi-arrow-right</v-icon>
      </button>
    </section>

    <section class="metrics-grid">
      <article v-for="metric in metrics" :key="metric.label" class="metric-card">
        <div>
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
          <small>{{ metric.helper }}</small>
        </div>
        <v-icon :color="metric.color" size="27">{{ metric.icon }}</v-icon>
      </article>
    </section>

    <section class="tabs-row">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="{ active: activeTab === tab.value, disabled: tab.disabled }"
        :disabled="tab.disabled"
        :title="tab.disabled ? 'Próximamente' : ''"
        @click="!tab.disabled && (activeTab = tab.value)"
      >
        {{ tab.label }}
        <span
          v-if="tab.value === 'review' && reviewPendingCount"
          class="tab-badge"
        >{{ reviewPendingCount }}</span>
      </button>
    </section>

    <section v-if="activeTab === 'portfolio'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Cartera por deudor</h2>
          <p>Prioriza por monto abierto, vencimiento, riesgo y último contacto.</p>
        </div>
        <v-btn text class="link-action" @click="uploadDialog = true">
          <v-icon left size="18">mdi-file-upload-outline</v-icon>
          Actualizar cartera
        </v-btn>
      </div>

      <div class="crm-toolbar">
        <label class="search-field">
          <v-icon size="20">mdi-magnify</v-icon>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Buscar por nombre, contacto, email o teléfono"
            @input="onSearchInput"
          />
        </label>

        <select v-model="filters.risk" @change="fetchDebtors">
          <option value="all">Todos los riesgos</option>
          <option value="Alto">Alto</option>
          <option value="Medio">Medio</option>
          <option value="Bajo">Bajo</option>
        </select>

        <select v-model="filters.channel" @change="fetchDebtors">
          <option value="all">Todos los canales</option>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Email">Email</option>
          <option value="Llamada">Llamada</option>
        </select>
      </div>

      <p v-if="debtorsPagination.has_more" class="pagination-hint">
        Mostrando los primeros {{ debtorsPagination.limit }} de {{ debtorsPagination.total }} deudores.
      </p>

      <v-data-table
        class="collections-table"
        :headers="portfolioHeaders"
        :items="filteredDebtors"
        :items-per-page="10"
        :loading="loadingDebtors"
        item-key="id"
        @click:row="openDebtor"
      >
        <template v-slot:item.name="{ item }">
          <div class="table-main-cell">
            <strong>{{ item.name }}</strong>
            <small>{{ item.contact }} · {{ item.channel }}</small>
          </div>
        </template>

        <template v-slot:item.openInvoices="{ item }">
          {{ item.openInvoiceCount }} abiertas
        </template>

        <template v-slot:item.agentStatus="{ item }">
          <em v-if="item.agentStatus" class="agent-status-tag">{{ item.agentStatus }}</em>
          <span v-else class="agent-status-empty">—</span>
        </template>

        <template v-slot:item.risk="{ item }">
          <em class="risk-tag" :class="item.riskClass">{{ item.risk }}</em>
        </template>
      </v-data-table>
    </section>

    <section v-if="activeTab === 'review'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Revisión de cambios del agente</h2>
          <p>
            Cambios de estatus aplicados automáticamente desde el agente de
            cobranza (Nexor) que requieren confirmación humana.
          </p>
        </div>
        <v-btn text class="link-action" :loading="loadingReview" @click="fetchReviewQueue">
          <v-icon left size="18">mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>

      <div v-if="loadingReview && !reviewQueue.length" class="empty-state">
        Cargando bandeja de revisión…
      </div>

      <div v-else-if="!reviewQueue.length" class="empty-state">
        No hay cambios pendientes de revisión.
      </div>

      <div v-else class="review-list">
        <article v-for="item in reviewQueue" :key="item.eventId" class="review-item">
          <div class="review-main">
            <strong>{{ item.debtorName }}</strong>
            <span>
              Factura {{ item.invoiceNumber }} · {{ item.amount }} · vence {{ item.dueDate }}
            </span>
            <small>
              {{ item.externalStatus || 'Cambio del agente' }}:
              {{ item.previousStatus }} → <b>{{ item.newStatus }}</b> · {{ item.occurredAt }}
            </small>
          </div>
          <div class="review-actions">
            <v-btn
              outlined
              class="details-button"
              :loading="resolvingReviewId === item.eventId"
              :disabled="resolvingReviewId === item.eventId"
              @click="resolveReview(item, 'revert')"
            >
              Revertir
            </v-btn>
            <v-btn
              class="primary-action small"
              elevation="0"
              :loading="resolvingReviewId === item.eventId"
              :disabled="resolvingReviewId === item.eventId"
              @click="resolveReview(item, 'confirm')"
            >
              Confirmar
            </v-btn>
          </div>
        </article>
      </div>
    </section>

    <section v-if="activeTab === 'invoices'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Facturas</h2>
          <p>Consulta facturas abiertas, vencidas, pagadas y con promesa de pago.</p>
        </div>
        <v-btn outlined class="details-button" @click="uploadDialog = true">
          <v-icon left size="18">mdi-file-plus-outline</v-icon>
          Cargar facturas
        </v-btn>
      </div>

      <div class="invoice-summary">
        <button
          v-for="status in invoiceStatusFilters"
          :key="status.value"
          type="button"
          :class="{ active: filters.invoiceStatus === status.value }"
          @click="filters.invoiceStatus = status.value"
        >
          <strong>{{ status.count }}</strong>
          <span>{{ status.label }}</span>
        </button>
      </div>

      <p v-if="invoicesPagination.has_more" class="pagination-hint">
        Mostrando las primeras {{ invoicesPagination.limit }} de {{ invoicesPagination.total }} facturas.
      </p>

      <v-data-table
        class="collections-table"
        :headers="invoiceHeaders"
        :items="filteredInvoices"
        :items-per-page="10"
        :loading="loadingInvoices"
        item-key="uid"
        @click:row="openInvoiceRow"
      >
        <template v-slot:item.status="{ item }">
          <em class="status-pill" :class="item.statusClass">{{ item.status }}</em>
        </template>
      </v-data-table>
    </section>

    <section v-if="activeTab === 'calendar'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Calendario de vencimientos</h2>
          <p>Visualiza qué cobrar hoy, esta semana y qué cartera ya está vencida.</p>
        </div>
        <div class="view-switch">
          <button
            type="button"
            :class="{ active: calendarViewMode === 'month' }"
            @click="calendarViewMode = 'month'"
          >
            <v-icon size="18">mdi-calendar-month-outline</v-icon>
            Mes
          </button>
          <button
            type="button"
            :class="{ active: calendarViewMode === 'agenda' }"
            @click="calendarViewMode = 'agenda'"
          >
            <v-icon size="18">mdi-view-column-outline</v-icon>
            Agenda
          </button>
        </div>
      </div>

      <div v-if="calendarViewMode === 'month'" class="month-calendar">
        <div class="month-calendar-header">
          <button type="button" class="month-nav" @click="goToPreviousMonth">
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <strong>{{ calendarMonthLabel }}</strong>
          <button type="button" class="month-nav" @click="goToNextMonth">
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="weekdays-row">
          <span v-for="day in weekDays" :key="day">{{ day }}</span>
        </div>

        <div class="month-grid">
          <div
            v-for="day in calendarMonthDays"
            :key="day.key"
            class="month-day"
            :class="{ muted: !day.currentMonth, today: day.isToday, clickable: day.invoices.length }"
            @click="openCalendarDay(day)"
          >
            <header>
              <span>{{ day.day }}</span>
              <strong v-if="day.invoices.length">{{ day.invoices.length }}</strong>
            </header>

            <button
              v-for="invoice in day.invoices.slice(0, 3)"
              :key="invoice.uid"
              type="button"
              class="calendar-event"
              :class="invoice.bucket"
              @click.stop="openCalendarDay(day)"
            >
              <span>{{ invoice.debtorName }}</span>
              <small>{{ invoice.id }} · {{ invoice.amount }}</small>
            </button>

            <em v-if="day.invoices.length > 3">
              +{{ day.invoices.length - 3 }} más
            </em>
          </div>
        </div>
      </div>

      <div v-else class="calendar-board agenda-board">
        <section v-for="bucket in calendarBuckets" :key="bucket.label" class="calendar-column">
          <header>
            <span>{{ bucket.label }}</span>
            <strong>{{ bucket.items.length }}</strong>
          </header>

          <button
            v-for="invoice in bucket.items"
            :key="invoice.uid"
            type="button"
            class="calendar-card"
            @click="openDebtorById(invoice.debtorId)"
          >
            <strong>{{ invoice.debtorName }}</strong>
            <span>{{ invoice.id }} · {{ invoice.amount }}</span>
            <small>{{ invoice.dueDate }} · {{ invoice.daysLabel }}</small>
            <em class="status-pill" :class="invoice.statusClass">{{ invoice.status }}</em>
          </button>

          <div v-if="!bucket.items.length" class="empty-state">Sin vencimientos</div>
        </section>
      </div>
    </section>

    <section v-if="activeTab === 'workflows'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Workflows automatizados</h2>
          <p>Configura cuándo y por qué canal debe actuar el agente.</p>
        </div>
        <v-btn class="secondary-action" elevation="0">
          <v-icon left size="18">mdi-plus</v-icon>
          Nuevo workflow
        </v-btn>
      </div>

      <div class="workflow-list">
        <article v-for="workflow in workflows" :key="workflow.name" class="workflow-row">
          <div>
            <h3>
              {{ workflow.name }}
              <span class="status-pill" :class="workflow.active ? 'success' : 'neutral'">
                {{ workflow.active ? 'Activo' : 'Pausado' }}
              </span>
            </h3>
            <p>{{ workflow.trigger }}</p>
            <div class="workflow-channels">
              <span v-for="channel in workflow.channels" :key="channel">{{ channel }}</span>
            </div>
          </div>
          <v-btn outlined class="details-button">Configurar</v-btn>
        </article>
      </div>
    </section>

    <section v-if="activeTab === 'history'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Historial del agente</h2>
          <p>Revisa cargas, cambios de estatus y conversaciones recientes.</p>
        </div>
      </div>

      <div class="timeline-list">
        <article v-for="event in history" :key="event.title" class="timeline-item">
          <span class="timeline-dot"></span>
          <div>
            <strong>{{ event.title }}</strong>
            <p>{{ event.description }}</p>
            <small>{{ event.date }}</small>
          </div>
        </article>
      </div>
    </section>

    <section v-if="activeTab === 'templates'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Template de estado de cuenta</h2>
          <p>
            Puedes cargar un estado de cuenta propio; Fresher AI normaliza
            deudores, facturas, vencimientos y cambios de estatus.
          </p>
        </div>
        <v-btn class="primary-action small" elevation="0" @click="downloadTemplate">
          <v-icon left size="18">mdi-download-outline</v-icon>
          Descargar ejemplo
        </v-btn>
      </div>

      <div class="template-grid">
        <article class="template-card">
          <h3>Columnas recomendadas</h3>
          <div class="columns-grid">
            <span v-for="column in templateColumns" :key="column">{{ column }}</span>
          </div>
        </article>

        <article class="template-card">
          <h3>Cómo lo procesa Fresher AI</h3>
          <ul>
            <li>Detecta deudores y facturas aunque el archivo venga en otro orden.</li>
            <li>Compara cargas nuevas contra la cartera anterior para detectar pagos.</li>
            <li>Clasifica facturas preventivas, vencidas, críticas y cerradas.</li>
            <li>Sincroniza contactos con el workflow de cobranza del proveedor externo.</li>
          </ul>
        </article>
      </div>
    </section>

    <v-navigation-drawer
      v-model="debtorDrawer"
      fixed
      right
      temporary
      width="680"
      class="debtor-drawer"
    >
      <div v-if="selectedDebtor" class="drawer-content">
        <header class="drawer-header">
          <div>
            <h2>{{ selectedDebtor.name }}</h2>
            <p>{{ selectedDebtor.openAmount }} abierto · {{ selectedDebtor.nextDue }}</p>
          </div>
          <button type="button" @click="debtorDrawer = false">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </header>

        <div class="drawer-tags">
          <span class="risk-tag" :class="selectedDebtor.riskClass">{{ selectedDebtor.risk }}</span>
          <span>{{ selectedDebtor.openInvoices.length }} facturas abiertas</span>
          <span>{{ selectedDebtor.channel }}</span>
        </div>

        <section class="drawer-panel aging-panel">
          <h3>Aging de cartera</h3>
          <div class="aging-grid">
            <article>
              <span>Por vencer</span>
              <strong>{{ selectedDebtor.aging.current }}</strong>
            </article>
            <article>
              <span>1-15 días</span>
              <strong>{{ selectedDebtor.aging.short }}</strong>
            </article>
            <article>
              <span>16-30 días</span>
              <strong>{{ selectedDebtor.aging.mid }}</strong>
            </article>
            <article>
              <span>30+ días</span>
              <strong>{{ selectedDebtor.aging.long }}</strong>
            </article>
          </div>
        </section>

        <section class="drawer-panel">
          <h3>Facturas abiertas</h3>
          <div class="drawer-invoice-table">
            <div class="drawer-invoice-row header">
              <span>Factura</span>
              <span>Monto</span>
              <span>Vence</span>
              <span>Estatus</span>
            </div>
            <template v-for="invoice in selectedDebtor.openInvoices">
              <div :key="invoice.id" class="drawer-invoice-row">
                <span>{{ invoice.id }}</span>
                <span>{{ invoice.amount }}</span>
                <span>{{ invoice.dueDate }}</span>
                <select v-model="invoice.status">
                  <option value="Pendiente">Pendiente</option>
                  <option value="En disputa">En disputa</option>
                  <option value="Pagado">Pagado</option>
                  <option value="Cancelada">Cancelada</option>
                </select>
              </div>
              <p
                v-if="invoice.requiresConfirmation"
                :key="`${invoice.id}-confirm`"
                class="invoice-confirm-note"
              >
                <v-icon size="14" color="#B45309">mdi-alert-outline</v-icon>
                Marcada como pagada por el agente — confirmar en la pestaña Revisión.
              </p>
            </template>
          </div>
        </section>

        <section class="drawer-panel">
          <h3>Promesas de pago</h3>
          <div v-if="loadingDebtorDetail && !selectedDebtor.promises.length" class="empty-state">
            Cargando promesas…
          </div>
          <div v-else-if="!selectedDebtor.promises.length" class="empty-state">
            Sin promesas de pago registradas.
          </div>
          <div v-else class="promise-list">
            <article
              v-for="promise in selectedDebtor.promises"
              :key="promise.id"
              class="promise-item"
            >
              <div>
                <strong>{{ promise.amount }}</strong>
                <span>Compromiso: {{ promise.date }}</span>
                <small v-if="promise.notes">{{ promise.notes }}</small>
              </div>
              <em class="status-pill neutral">{{ promise.status }}</em>
            </article>
          </div>
        </section>

        <section class="drawer-panel chat-panel">
          <div class="chat-heading">
            <div>
              <h3>Conversación</h3>
              <p>{{ selectedDebtor.channel }} · Workflow {{ selectedDebtor.workflow }}</p>
            </div>
            <v-icon size="22">mdi-whatsapp</v-icon>
          </div>

          <div class="whatsapp-chat">
            <div v-if="loadingDebtorHistory" class="empty-state">
              Cargando historial…
            </div>

            <div v-else-if="!selectedDebtor.conversations.length" class="empty-state">
              Historial de mensajes no disponible aún.
            </div>

            <template v-else>
              <article
                v-for="message in selectedDebtor.conversations"
                :key="message.id"
                class="chat-message"
                :class="message.type"
              >
                <div v-if="message.type === 'system'" class="chat-system-wrapper">
                  <div class="chat-system-line">
                    <span></span>
                    <strong>{{ message.text }}</strong>
                    <span></span>
                  </div>
                  <small class="chat-system-date">{{ message.date }}</small>
                </div>

                <div v-else class="chat-bubble">
                  <strong>{{ message.sender }}</strong>
                  <p>{{ message.text }}</p>
                  <small>
                    {{ message.date }}
                    <v-icon v-if="message.type === 'outgoing'" size="14">mdi-check-all</v-icon>
                  </small>
                </div>
              </article>
            </template>
          </div>
        </section>

        <footer class="drawer-actions">
          <v-btn outlined class="details-button">
            <v-icon left size="18">mdi-calendar-check-outline</v-icon>
            Promesa de pago
          </v-btn>
          <v-btn outlined class="details-button">
            <v-icon left size="18">mdi-email-outline</v-icon>
            Correo
          </v-btn>
          <v-btn
            class="primary-action small"
            elevation="0"
            :loading="savingInvoiceChanges"
            @click="saveInvoiceChanges"
          >
            Guardar cambios
          </v-btn>
        </footer>
      </div>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-model="calendarDayDrawer"
      fixed
      right
      temporary
      width="560"
      class="debtor-drawer"
    >
      <div v-if="selectedCalendarDay" class="drawer-content day-detail-drawer">
        <header class="drawer-header">
          <div>
            <h2>{{ selectedCalendarDay.title }}</h2>
            <p>
              {{ selectedCalendarDay.invoices.length }} facturas ·
              {{ selectedCalendarDay.totalAmount }} por cobrar
            </p>
          </div>
          <button type="button" @click="calendarDayDrawer = false">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </header>

        <section class="drawer-panel">
          <h3>Facturas con vencimiento</h3>
          <div class="day-invoice-list">
            <button
              v-for="invoice in selectedCalendarDay.invoices"
              :key="invoice.uid"
              type="button"
              class="day-invoice-card"
              @click="openDebtorFromDay(invoice.debtorId)"
            >
              <div>
                <strong>{{ invoice.debtorName }}</strong>
                <span>{{ invoice.id }} · {{ invoice.amount }}</span>
                <small>{{ invoice.workflow }} · {{ invoice.daysLabel }}</small>
              </div>
              <em class="status-pill" :class="invoice.statusClass">{{ invoice.status }}</em>
            </button>
          </div>
        </section>

        <section class="drawer-panel day-summary-panel">
          <h3>Lectura rápida</h3>
          <div class="day-summary-grid">
            <article>
              <span>Pendientes</span>
              <strong>{{ selectedCalendarDay.pendingCount }}</strong>
            </article>
            <article>
              <span>Promesas</span>
              <strong>{{ selectedCalendarDay.promiseCount }}</strong>
            </article>
            <article>
              <span>Disputas</span>
              <strong>{{ selectedCalendarDay.disputeCount }}</strong>
            </article>
          </div>
        </section>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="uploadDialog" max-width="860">
      <v-card class="upload-dialog">
        <v-card-title>
          <div>
            <h2>Actualizar cartera</h2>
            <p>
              Carga un Excel con deudores, facturas, deuda y vencimientos.
              Usa la plantilla base para asegurar que las columnas coincidan.
            </p>
          </div>
        </v-card-title>

        <v-card-text>
          <div class="upload-grid">
            <button type="button" class="dropzone" @click="$refs.accountFile.click()">
              <v-icon color="#0F82C3" size="38">mdi-cloud-upload-outline</v-icon>
              <strong>{{ selectedFileName || 'Seleccionar archivo' }}</strong>
              <span>Formato requerido: .xlsx</span>
            </button>
            <input
              ref="accountFile"
              type="file"
              accept=".xlsx"
              class="hidden-input"
              @change="handleFileChange"
            />

            <div class="upload-options">
              <label>
                <span>Tipo de carga</span>
                <select v-model="uploadForm.loadType">
                  <option value="new">Nueva cartera</option>
                  <option value="update">Actualizar cartera existente</option>
                </select>
              </label>

              <label>
                <span>Workflow inicial</span>
                <select v-model="uploadForm.workflow">
                  <option value="preventive">Cobranza preventiva</option>
                  <option value="overdue">Cobranza vencida</option>
                  <option value="mixed">Preventiva + vencida</option>
                </select>
              </label>

              <label class="checkbox-row">
                <input v-model="uploadForm.autoFormat" type="checkbox" />
                <span>Permitir que IA formatee columnas automáticamente</span>
              </label>
            </div>
          </div>

          <div class="mapping-preview">
            <h3>Vista previa esperada</h3>
            <div class="preview-table">
              <div v-for="row in previewRows" :key="row.invoice" class="preview-row">
                <span>{{ row.client }}</span>
                <span>{{ row.invoice }}</span>
                <span>{{ row.amount }}</span>
                <span>{{ row.dueDate }}</span>
                <span>{{ row.status }}</span>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="uploadDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined class="details-button" @click="downloadTemplate">
            Descargar template
          </v-btn>
          <v-btn
            class="primary-action small"
            elevation="0"
            :loading="importingInvoices"
            :disabled="importingInvoices || !selectedFile"
            @click="processImportFile"
          >
            Procesar archivo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'

const RISK_UI_TO_API = { Alto: 'high', Medio: 'medium', Bajo: 'low' }
const RISK_API_TO_UI = { high: 'Alto', medium: 'Medio', low: 'Bajo' }
const CHANNEL_UI_TO_API = { WhatsApp: 'whatsapp', Email: 'email', Llamada: 'phone' }
const CHANNEL_API_TO_UI = { whatsapp: 'WhatsApp', email: 'Email', phone: 'Llamada' }
const CHANNEL_ICONS = { WhatsApp: 'mdi-whatsapp', Email: 'mdi-email-outline', Llamada: 'mdi-phone-outline' }
const INVOICE_STATUS_UI_TO_API = {
  Pendiente: 'pending',
  'En disputa': 'disputed',
  Pagado: 'paid',
  Cancelada: 'cancelled',
  open: 'open',
}
const INVOICE_STATUS_API_TO_UI = {
  pending: 'Pendiente',
  overdue: 'Vencida',
  paid: 'Pagado',
  disputed: 'En disputa',
  cancelled: 'Cancelada',
}
const INVOICE_STATUS_CLASS = {
  Pendiente: 'neutral',
  Vencida: 'warning',
  'En disputa': 'warning',
  Pagado: 'success',
  Cancelada: 'neutral',
}
const AGENT_STATUS_LABELS = {
  uncontacted: 'Sin contactar',
  contacted: 'Contactado',
  contacting: 'En contacto',
  engaged: 'En conversación',
  promise_to_pay: 'Promesa de pago',
  follow_up: 'En seguimiento',
  disputed: 'En disputa',
  dispute: 'En disputa',
  paid: 'Pago afirmado',
  converted: 'Pago afirmado',
  unreachable: 'Ilocalizable',
  archived: 'Archivado',
}

function agentStatusLabel(rawKey, rawLabel) {
  if (!rawKey && !rawLabel) return null
  const key = String(rawKey || '').toLowerCase()
  return AGENT_STATUS_LABELS[key] || rawLabel || rawKey || null
}

function timeAgoLabel(isoDate) {
  if (!isoDate) return null
  const then = new Date(isoDate)
  if (Number.isNaN(then.getTime())) return null
  const diffMin = Math.round((Date.now() - then.getTime()) / 60000)
  if (diffMin < 1) return 'hace instantes'
  if (diffMin < 60) return `hace ${diffMin} min`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `hace ${diffH} h`
  const diffD = Math.round(diffH / 24)
  return `hace ${diffD} d`
}

export default {
  name: 'CollectionsAgent',
  data() {
    return {
      syncingDebtors: false,
      importingInvoices: false,
      loadingSummary: false,
      loadingDebtors: false,
      loadingInvoices: false,
      loadingCalendar: false,
      loadingDebtorDetail: false,
      loadingDebtorHistory: false,
      savingInvoiceChanges: false,
      loadingReview: false,
      resolvingReviewId: null,
      invoicesLoaded: false,
      calendarLoaded: false,
      reviewLoaded: false,
      reviewQueue: [],
      activeTab: 'portfolio',
      calendarViewMode: 'month',
      uploadDialog: false,
      debtorDrawer: false,
      calendarDayDrawer: false,
      selectedDebtor: null,
      selectedCalendarDay: null,
      selectedFileName: '',
      selectedFile: null,
      summary: null,
      invoices: [],
      invoicesPagination: { page: 1, limit: 200, total: 0, has_more: false },
      debtorsPagination: { page: 1, limit: 100, total: 0, has_more: false },
      calendarInvoices: [],
      calendarYear: new Date().getFullYear(),
      calendarMonth: new Date().getMonth(),
      _searchDebounce: null,
      _originalInvoiceStatuses: null,
      filters: {
        search: '',
        risk: 'all',
        channel: 'all',
        invoiceStatus: 'open',
      },
      weekDays: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      uploadForm: {
        loadType: 'update',
        workflow: 'mixed',
        autoFormat: true,
      },
      tabs: [
        { label: 'Cartera', value: 'portfolio' },
        { label: 'Facturas', value: 'invoices' },
        { label: 'Calendario', value: 'calendar' },
        { label: 'Revisión', value: 'review' },
        { label: 'Workflows', value: 'workflows', disabled: true },
        { label: 'Historial', value: 'history' },
        { label: 'Templates', value: 'templates', disabled: true },
      ],
      portfolioHeaders: [
        { text: 'Deudor', value: 'name' },
        { text: 'Total abierto', value: 'openAmount' },
        { text: 'Vencido', value: 'overdueAmount' },
        { text: 'Facturas', value: 'openInvoices', sortable: false },
        { text: 'Próximo vencimiento', value: 'nextDue' },
        { text: 'Último contacto', value: 'lastContact' },
        { text: 'Estado agente', value: 'agentStatus', sortable: false },
        { text: 'Riesgo', value: 'risk' },
      ],
      invoiceHeaders: [
        { text: 'Factura', value: 'id' },
        { text: 'Deudor', value: 'debtorName' },
        { text: 'Monto', value: 'amount' },
        { text: 'Vence', value: 'dueDate' },
        { text: 'Días', value: 'daysLabel' },
        { text: 'Estatus', value: 'status' },
        { text: 'Workflow', value: 'workflow' },
      ],
      debtors: [],
      workflows: [
        {
          name: 'Cobranza preventiva',
          trigger: 'Trigger: factura vence en 5 días',
          active: true,
          channels: ['Email', 'WhatsApp'],
        },
        {
          name: 'Seguimiento vencido',
          trigger: 'Trigger: factura vencida por más de 3 días',
          active: true,
          channels: ['WhatsApp', 'Llamada', 'Email'],
        },
        {
          name: 'Escalación crítica',
          trigger: 'Trigger: deuda mayor a $50,000 o vencida por más de 15 días',
          active: false,
          channels: ['Llamada', 'Notificación interna'],
        },
      ],
      history: [
        {
          title: 'Estado de cuenta procesado',
          description: 'Se detectaron 20 deudores, 29 facturas abiertas y 7 cambios de estatus.',
          date: 'Hoy 09:42',
        },
        {
          title: 'Promesa de pago registrada',
          description: 'Empresa ABC S.A. prometió pago parcial para el 29 Jul 2026.',
          date: 'Ayer 16:10',
        },
        {
          title: 'Workflow preventivo activado',
          description: 'Se enviaron 18 recordatorios por WhatsApp y correo.',
          date: '06 Jul 2026',
        },
      ],
      templateColumns: [
        'RFC / Tax ID',
        'Razón Social',
        'Nombre Comercial',
        'Contacto',
        'Email',
        'Teléfono',
        'Canal Preferido',
        'Nivel de Riesgo',
        'Notas Deudor',
        'Número de Factura',
        'Referencia Externa',
        'Moneda',
        'Monto Original',
        'Monto Pagado',
        'Fecha de Emisión',
        'Fecha de Vencimiento',
        'Estatus Factura',
      ],
      previewRows: [
        {
          client: 'Empresa ABC S.A.',
          invoice: 'FAC-1001',
          amount: '$18,000',
          dueDate: '24 Jul 2026',
          status: 'Pendiente',
        },
        {
          client: 'Distribuidora XYZ',
          invoice: 'FAC-2210',
          amount: '$32,000',
          dueDate: '16 Jul 2026',
          status: 'Vencida',
        },
      ],
    }
  },
  mounted() {
    this.fetchSummary()
    this.fetchDebtors()
    this.fetchReviewQueue()

    this._postSyncRefresh = setTimeout(() => {
      this.fetchSummary({ triggerSync: false })
      this.fetchDebtors()
      this.fetchReviewQueue()
    }, 6000)
  },
  beforeDestroy() {
    clearTimeout(this._postSyncRefresh)
    clearTimeout(this._searchDebounce)
  },
  watch: {
    activeTab(value) {
      if (value === 'invoices' && !this.invoicesLoaded) {
        this.fetchInvoices()
      } else if (value === 'calendar' && !this.calendarLoaded) {
        this.fetchCalendar()
      } else if (value === 'review') {
        this.fetchReviewQueue()
      }
    },
    'filters.invoiceStatus': function onInvoiceStatusChange() {
      if (this.activeTab === 'invoices') this.fetchInvoices()
    },
  },
  computed: {
    metrics() {
      const currencies = this.summary?.currencies || []
      const primary = currencies.length
        ? currencies.reduce((max, item) => (Number(item.total_open) > Number(max.total_open) ? item : max), currencies[0])
        : null
      const extraCurrencies = currencies.length > 1 ? ` · +${currencies.length - 1} moneda(s)` : ''

      return [
        {
          label: 'Total abierto',
          value: primary ? this.formatCurrency(primary.total_open, primary.currency) : '$0',
          helper: `${this.summary?.total_debtors || 0} deudores activos${extraCurrencies}`,
          icon: 'mdi-currency-usd',
          color: '#246BFE',
        },
        {
          label: 'Vencido',
          value: primary ? this.formatCurrency(primary.overdue, primary.currency) : '$0',
          helper: `${this.summary?.overdue_debtors || 0} deudores con mora`,
          icon: 'mdi-alert-circle-outline',
          color: '#DC2626',
        },
        {
          label: 'Por vencer 7 días',
          value: primary ? this.formatCurrency(primary.due_next_7_days, primary.currency) : '$0',
          helper: 'Cobranza preventiva',
          icon: 'mdi-calendar-clock',
          color: '#F59E0B',
        },
        {
          label: 'Pagado (mes)',
          value: primary ? this.formatCurrency(primary.paid_this_month, primary.currency) : '$0',
          helper: `${this.summary?.invoice_status_counts?.paid || 0} facturas cerradas`,
          icon: 'mdi-check-circle-outline',
          color: '#16A34A',
        },
      ]
    },
    filteredDebtors() {
      return this.debtors
    },
    filteredInvoices() {
      return this.invoices
    },
    invoiceStatusFilters() {
      const counts = this.summary?.invoice_status_counts || {}
      const openCount = Object.entries(counts)
        .filter(([status]) => !['paid', 'cancelled'].includes(status))
        .reduce((sum, [, count]) => sum + count, 0)

      return [
        { label: 'Abiertas', value: 'open', count: openCount },
        { label: 'Pendientes', value: 'Pendiente', count: counts.pending || 0 },
        { label: 'Disputa', value: 'En disputa', count: counts.disputed || 0 },
        { label: 'Pagadas', value: 'Pagado', count: counts.paid || 0 },
      ]
    },
    calendarInvoicesMapped() {
      return this.calendarInvoices.map(this.mapInvoiceFromApi)
    },
    calendarBuckets() {
      const openInvoices = this.calendarInvoicesMapped.filter((invoice) => invoice.status !== 'Pagado')
      return [
        { label: 'Vencidas', items: openInvoices.filter((invoice) => invoice.bucket === 'overdue') },
        { label: 'Hoy / mañana', items: openInvoices.filter((invoice) => invoice.bucket === 'today') },
        { label: 'Esta semana', items: openInvoices.filter((invoice) => invoice.bucket === 'week') },
        { label: 'Próximos 15 días', items: openInvoices.filter((invoice) => invoice.bucket === 'soon') },
      ]
    },
    calendarMonthLabel() {
      const label = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' })
        .format(new Date(this.calendarYear, this.calendarMonth, 1))
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    calendarMonthDays() {
      const year = this.calendarYear
      const monthIndex = this.calendarMonth
      const firstDay = new Date(year, monthIndex, 1)
      const startOffset = (firstDay.getDay() + 6) % 7
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
      const today = new Date()
      const cells = []

      for (let i = 0; i < startOffset; i += 1) {
        cells.push({
          key: `prev-${i}`,
          day: '',
          currentMonth: false,
          isToday: false,
          invoices: [],
        })
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const isToday = today.getFullYear() === year
          && today.getMonth() === monthIndex
          && today.getDate() === day

        cells.push({
          key: `${year}-${monthIndex}-${day}`,
          day,
          currentMonth: true,
          isToday,
          invoices: this.calendarInvoicesMapped.filter((invoice) => (
            invoice.dueDateRaw
            && invoice.dueDateRaw.getFullYear() === year
            && invoice.dueDateRaw.getMonth() === monthIndex
            && invoice.dueDateRaw.getDate() === day
          )),
        })
      }

      while (cells.length % 7 !== 0) {
        cells.push({
          key: `next-${cells.length}`,
          day: '',
          currentMonth: false,
          isToday: false,
          invoices: [],
        })
      }

      return cells
    },
    lastSyncLabel() {
      return timeAgoLabel(this.summary?.last_synced_at)
    },
    reviewPendingCount() {
      return this.reviewQueue.length
    },
  },
  methods: {
    getRequestConfig(params) {
      return {
        params,
        headers: {
          Authorization: this.$store.state.sToken
            ? `Bearer ${this.$store.state.sToken}`
            : '',
        },
      }
    },
    async syncDebtors() {
      this.syncingDebtors = true

      try {
        const response = await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/syncDebtors`,
          {
            agent_type: 'collections',
          },
          this.getRequestConfig()
        )
        const summary = response.data?.data || {}

        this.mixSuccess(
          `Sincronización de Cobranza completa: ${summary.created || 0} nuevos, `
          + `${summary.updated || 0} actualizados.`
        )
      } catch (error) {
        const message = error.response?.data?.message
          || 'No fue posible sincronizar los contactos de cobranza.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.syncingDebtors = false
      }
    },
    formatCurrency(amount, currency) {
      const value = Number(amount) || 0
      const formatted = value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      return currency ? `$${formatted} ${currency}` : `$${formatted}`
    },
    formatDate(isoDate) {
      if (!isoDate) return 'Sin fecha'
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    computeBucket(dueDateRaw, isClosed) {
      if (isClosed) return 'closed'
      if (!dueDateRaw) return 'soon'

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const due = new Date(dueDateRaw)
      due.setHours(0, 0, 0, 0)
      const diffDays = Math.round((due - today) / 86400000)

      if (diffDays < 0) return 'overdue'
      if (diffDays <= 1) return 'today'
      if (diffDays <= 7) return 'week'
      return 'soon'
    },
    mapInvoiceFromApi(inv) {
      const isClosed = ['paid', 'cancelled'].includes(inv.status)
      const dueDateRaw = inv.due_date ? new Date(inv.due_date) : null
      const status = INVOICE_STATUS_API_TO_UI[inv.status] || inv.status
      const daysLabel = {
        overdue: 'Vencida',
        paid: 'Cerrada',
        cancelled: 'Cerrada',
      }[inv.status] || 'Por vencer'

      return {
        id: inv.invoice_number,
        uid: inv.id,
        debtorId: inv.debtor_id,
        debtorName: inv.debtor_name,
        amount: this.formatCurrency(inv.outstanding_amount, inv.currency),
        currency: inv.currency,
        dueDate: this.formatDate(inv.due_date),
        dueDateRaw,
        status,
        statusClass: INVOICE_STATUS_CLASS[status] || 'neutral',
        workflow: inv.workflow_id ? 'Con workflow' : 'Sin workflow',
        daysLabel,
        bucket: this.computeBucket(dueDateRaw, isClosed),
        requiresConfirmation: Boolean(inv.metadata && inv.metadata.requires_confirmation),
      }
    },
    mapDebtorFromApi(d) {
      const channel = CHANNEL_API_TO_UI[d.preferred_channel] || 'Sin canal'
      const risk = RISK_API_TO_UI[d.risk_level] || 'Bajo'

      return {
        id: d.id,
        name: d.legal_name || d.trade_name || 'Sin nombre',
        contact: d.contact_name || 'Sin contacto',
        openAmount: this.formatCurrency(d.open_amount, null),
        overdueAmount: this.formatCurrency(d.overdue_amount, null),
        nextDue: d.next_due_date ? this.formatDate(d.next_due_date) : 'Sin vencimientos',
        channel,
        channelIcon: CHANNEL_ICONS[channel] || 'mdi-message-outline',
        risk,
        riskClass: d.risk_level || 'low',
        workflow: d.workflow_id ? 'Con workflow' : 'Sin workflow',
        lastContact: d.last_contacted_at ? this.formatDate(d.last_contacted_at) : 'Sin contacto',
        agentStatus: agentStatusLabel(
          d.metadata && d.metadata.external_status,
          d.metadata && d.metadata.external_status_label,
        ),
        aging: { current: '$0', short: '$0', mid: '$0', long: '$0' },
        openInvoiceCount: d.open_invoice_count || 0,
        openInvoices: [],
        paidInvoices: [],
        conversations: [],
        promises: [],
      }
    },
    async fetchSummary({ triggerSync = true } = {}) {
      this.loadingSummary = true
      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/summary`,
          this.getRequestConfig(triggerSync ? undefined : { sync: 'false' })
        )
        this.summary = response.data?.data || null
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar el resumen de cobranza.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingSummary = false
      }
    },
    async fetchDebtors() {
      this.loadingDebtors = true
      try {
        const params = {
          page: 0,
          limit: this.debtorsPagination.limit,
        }
        if (this.filters.search.trim()) params.search = this.filters.search.trim()
        if (this.filters.risk !== 'all') params.risk_level = RISK_UI_TO_API[this.filters.risk]
        if (this.filters.channel !== 'all') params.channel = CHANNEL_UI_TO_API[this.filters.channel]

        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/debtors`,
          this.getRequestConfig(params)
        )
        const data = response.data?.data || {}
        this.debtors = (data.debtors || []).map(this.mapDebtorFromApi)
        this.debtorsPagination = data.pagination || this.debtorsPagination
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar la cartera de deudores.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingDebtors = false
      }
    },
    async fetchInvoices() {
      this.loadingInvoices = true
      try {
        const params = {
          page: 0,
          limit: this.invoicesPagination.limit,
        }
        const status = INVOICE_STATUS_UI_TO_API[this.filters.invoiceStatus]
        if (status) params.status = status

        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/invoices`,
          this.getRequestConfig(params)
        )
        const data = response.data?.data || {}
        this.invoices = (data.invoices || []).map(this.mapInvoiceFromApi)
        this.invoicesPagination = data.pagination || this.invoicesPagination
        this.invoicesLoaded = true
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar las facturas.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingInvoices = false
      }
    },
    async fetchCalendar() {
      this.loadingCalendar = true
      try {
        const firstDay = new Date(this.calendarYear, this.calendarMonth, 1)
        const lastDay = new Date(this.calendarYear, this.calendarMonth + 1, 0)
        const toIso = (date) => date.toISOString().slice(0, 10)

        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/calendar`,
          this.getRequestConfig({ date_from: toIso(firstDay), date_to: toIso(lastDay) })
        )
        this.calendarInvoices = response.data?.data?.invoices || []
        this.calendarLoaded = true
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar el calendario de cobranza.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingCalendar = false
      }
    },
    async fetchReviewQueue() {
      this.loadingReview = true
      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/reviewQueue`,
          this.getRequestConfig()
        )
        this.reviewQueue = (response.data?.data?.items || []).map((item) => ({
          eventId: item.event_id,
          invoiceId: item.invoice_id,
          invoiceNumber: item.invoice_number,
          debtorName: item.debtor_name,
          previousStatus: INVOICE_STATUS_API_TO_UI[item.previous_status] || item.previous_status,
          newStatus: INVOICE_STATUS_API_TO_UI[item.new_status] || item.new_status,
          amount: this.formatCurrency(item.outstanding_amount || item.original_amount, item.currency),
          dueDate: this.formatDate(item.due_date),
          occurredAt: item.occurred_at
            ? new Date(item.occurred_at).toLocaleString('es-MX', {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            })
            : '',
          externalStatus: agentStatusLabel(
            item.event_metadata && item.event_metadata.external_status,
            null,
          ),
        }))
        this.reviewLoaded = true
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar la bandeja de revisión.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingReview = false
      }
    },
    async resolveReview(item, action) {
      this.resolvingReviewId = item.eventId
      try {
        await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/reviewQueue/${item.invoiceId}/resolve`,
          { action },
          this.getRequestConfig()
        )
        this.mixSuccess(action === 'confirm' ? 'Cambio confirmado.' : 'Cambio revertido.')
        await this.fetchReviewQueue()
        this.fetchSummary()
        this.fetchDebtors()
        if (this.activeTab === 'invoices') this.fetchInvoices()
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible resolver el elemento.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.resolvingReviewId = null
      }
    },
    async fetchDebtorDetail(debtorId) {
      this.loadingDebtorDetail = true
      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/debtors/${debtorId}`,
          this.getRequestConfig()
        )
        const detail = response.data?.data || {}
        if (!this.selectedDebtor || this.selectedDebtor.id !== debtorId) return

        const invoices = (detail.invoices || []).map(this.mapInvoiceFromApi)
        const agingList = detail.aging || []
        const aging = agingList.length
          ? {
            current: this.formatCurrency(agingList[0].current, agingList[0].currency),
            short: this.formatCurrency(agingList[0].short, agingList[0].currency),
            mid: this.formatCurrency(agingList[0].mid, agingList[0].currency),
            long: this.formatCurrency(agingList[0].long, agingList[0].currency),
          }
          : this.selectedDebtor.aging

        const promises = (detail.promises || []).map((promise) => ({
          id: promise.id,
          amount: promise.promised_amount
            ? this.formatCurrency(promise.promised_amount, promise.currency)
            : 'Monto no especificado',
          date: this.formatDate(promise.promised_date),
          status: promise.status,
          notes: promise.notes || '',
        }))

        this.selectedDebtor = {
          ...this.selectedDebtor,
          openInvoices: invoices.filter((invoice) => invoice.status !== 'Pagado'),
          paidInvoices: invoices.filter((invoice) => invoice.status === 'Pagado'),
          promises,
          aging,
        }
        this._originalInvoiceStatuses = new Map(invoices.map((invoice) => [invoice.uid, invoice.status]))
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar el detalle del deudor.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingDebtorDetail = false
      }
    },
    mapHistoryFromApi(item) {
      const date = new Date(item.timestamp)
      const dateLabel = Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleString('es-MX', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        })

      return {
        id: item.id,
        type: item.type,
        sender: item.sender,
        text: item.text,
        date: dateLabel,
      }
    },
    async fetchDebtorHistory(debtorId) {
      this.loadingDebtorHistory = true
      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/debtors/${debtorId}/history`,
          this.getRequestConfig()
        )
        if (!this.selectedDebtor || this.selectedDebtor.id !== debtorId) return

        const history = response.data?.data?.history || []
        this.selectedDebtor = {
          ...this.selectedDebtor,
          conversations: history.map(this.mapHistoryFromApi),
        }
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible cargar el historial de contacto.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.loadingDebtorHistory = false
      }
    },
    async saveInvoiceChanges() {
      if (!this.selectedDebtor || !this._originalInvoiceStatuses) return

      const allInvoices = [
        ...this.selectedDebtor.openInvoices,
        ...this.selectedDebtor.paidInvoices,
      ]
      const changed = allInvoices.filter((invoice) => (
        this._originalInvoiceStatuses.get(invoice.uid) !== invoice.status
      ))

      if (!changed.length) {
        this.mixError('No hay cambios por guardar')
        return
      }

      this.savingInvoiceChanges = true

      const results = await Promise.allSettled(
        changed.map((invoice) => DB.patch(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/invoices/${invoice.uid}`,
          { status: INVOICE_STATUS_UI_TO_API[invoice.status] },
          this.getRequestConfig()
        ))
      )

      const failed = results.filter((result) => result.status === 'rejected')
      if (failed.length) {
        this.mixError(`No fue posible actualizar ${failed.length} factura(s).`, 400)
      }
      if (failed.length < changed.length) {
        this.mixSuccess('Cambios guardados')
      }

      const debtorId = this.selectedDebtor.id
      await this.fetchDebtorDetail(debtorId)
      this.fetchSummary()
      this.fetchDebtors()

      this.savingInvoiceChanges = false
    },
    onSearchInput() {
      clearTimeout(this._searchDebounce)
      this._searchDebounce = setTimeout(() => {
        this.fetchDebtors()
      }, 400)
    },
    goToPreviousMonth() {
      if (this.calendarMonth === 0) {
        this.calendarMonth = 11
        this.calendarYear -= 1
      } else {
        this.calendarMonth -= 1
      }
      this.fetchCalendar()
    },
    goToNextMonth() {
      if (this.calendarMonth === 11) {
        this.calendarMonth = 0
        this.calendarYear += 1
      } else {
        this.calendarMonth += 1
      }
      this.fetchCalendar()
    },
    openDebtor(debtor) {
      this.selectedDebtor = debtor
      this.debtorDrawer = true
      this.fetchDebtorDetail(debtor.id)
      this.fetchDebtorHistory(debtor.id)
    },
    openDebtorById(id) {
      const debtor = this.debtors.find((item) => item.id === id)
      if (debtor) this.openDebtor(debtor)
    },
    openInvoiceRow(invoice) {
      this.openDebtorById(invoice.debtorId)
    },
    openCalendarDay(day) {
      if (!day.currentMonth || !day.invoices.length) return

      this.selectedCalendarDay = {
        title: `${day.day} de ${this.calendarMonthLabel}`,
        invoices: day.invoices,
        totalAmount: this.sumInvoiceAmounts(day.invoices),
        pendingCount: day.invoices.filter((invoice) => invoice.status === 'Pendiente').length,
        promiseCount: 0,
        disputeCount: day.invoices.filter((invoice) => invoice.status === 'En disputa').length,
      }
      this.calendarDayDrawer = true
    },
    openDebtorFromDay(debtorId) {
      this.calendarDayDrawer = false
      this.openDebtorById(debtorId)
    },
    sumInvoiceAmounts(invoices) {
      const total = invoices.reduce((sum, invoice) => {
        const amount = Number(String(invoice.amount).replace(/[^0-9.-]/g, '')) || 0
        return sum + amount
      }, 0)

      return `$${total.toLocaleString('en-US')}`
    },
    openCreateDebtor() {
      this.uploadDialog = true
    },
    handleFileChange(event) {
      const file = event.target.files[0] || null
      this.selectedFile = file
      this.selectedFileName = file ? file.name : ''
    },
    downloadTemplate() {
      const exampleRows = [
        {
          'RFC / Tax ID': 'EAB010101AAA',
          'Razón Social': 'Empresa ABC S.A.',
          'Nombre Comercial': 'ABC',
          Contacto: 'Ana Gomez',
          Email: 'cobranza@empresaabc.com',
          Teléfono: '5555555555',
          'Canal Preferido': 'whatsapp',
          'Nivel de Riesgo': 'low',
          'Notas Deudor': 'Cliente con pago recurrente',
          'Número de Factura': 'FAC-1001',
          'Referencia Externa': '',
          Moneda: 'MXN',
          'Monto Original': 18000,
          'Monto Pagado': 0,
          'Fecha de Emisión': '2026-07-01',
          'Fecha de Vencimiento': '2026-07-24',
          'Estatus Factura': '',
        },
      ]

      const worksheet = XLSX.utils.json_to_sheet(exampleRows, { header: this.templateColumns })
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Estado de cuenta')

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

      saveAs(blob, 'template_estado_de_cuenta_fresher.xlsx')
    },
    async processImportFile() {
      if (!this.selectedFile) {
        this.mixError('Selecciona un archivo antes de continuar')
        return
      }

      this.importingInvoices = true

      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)

        const response = await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/collections/importInvoices`,
          formData,
          {
            headers: {
              Authorization: this.$store.state.sToken
                ? `Bearer ${this.$store.state.sToken}`
                : '',
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        const summary = response.data?.data || {}

        this.mixSuccess(
          `Estado de cuenta importado: ${summary.created_rows || 0} creados, `
          + `${summary.updated_rows || 0} actualizados, ${summary.failed_rows || 0} con error.`
        )

        this.uploadDialog = false
        this.selectedFile = null
        this.selectedFileName = ''
      } catch (error) {
        const message = error.response?.data?.message
          || 'No fue posible importar el estado de cuenta.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.importingInvoices = false
      }
    },
  },
}
</script>

<style scoped>
.collections-agent {
  min-height: 100vh;
  padding: 46px 56px 72px;
  background: #f8fafc;
  color: #09090b;
  font-family: Inter, Roboto, Arial, sans-serif;
}

.collections-header,
.collections-title,
.header-actions,
.card-heading,
.tabs-row {
  display: flex;
  align-items: center;
}

.collections-header {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}

.collections-title,
.header-actions {
  gap: 16px;
}

.agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: linear-gradient(135deg, #0f82c3, #246bfe);
  box-shadow: 0 14px 28px rgba(15, 130, 195, 0.22);
}

h1,
h2,
h3,
p {
  margin: 0;
}

.collections-title h1 {
  font-size: 38px;
  font-weight: 900;
  letter-spacing: 0;
}

.collections-title p,
.card-heading p {
  margin-top: 8px;
  color: #475569;
  font-size: 17px;
  font-weight: 600;
}

.primary-action,
.secondary-action {
  min-height: 48px !important;
  border-radius: 10px !important;
  background: linear-gradient(90deg, #246bfe, #0f82c3) !important;
  color: #fff !important;
  font-family: "montserrat-semibold";
  font-size: 15px !important;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action.small {
  min-height: 40px !important;
}

.secondary-action {
  background: #0f82c3 !important;
}

.details-button {
  min-height: 40px !important;
  border-radius: 10px !important;
  border-color: #d8dee8 !important;
  color: #18181b !important;
  font-family: "montserrat-semibold";
  text-transform: none;
}

.insight-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 28px;
  padding: 18px 22px;
  border: 1px solid rgba(15, 130, 195, 0.16);
  border-radius: 16px;
  background:
    linear-gradient(90deg, rgba(15, 130, 195, 0.1), rgba(36, 107, 254, 0.06)),
    #ffffff;
}

.insight-banner span {
  display: block;
  margin-bottom: 4px;
  color: #0f82c3;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.insight-banner strong {
  color: #0f172a;
  font-size: 16px;
}

.insight-banner button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: #0f82c3;
  cursor: pointer;
  font-weight: 900;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 34px;
}

.metric-card,
.content-card,
.upload-dialog {
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.metric-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 164px;
  padding: 26px 28px;
}

.metric-card span,
.metric-card small {
  display: block;
  color: #64748b;
  font-weight: 800;
}

.metric-card strong {
  display: block;
  margin: 28px 0 16px;
  color: #020617;
  font-size: 30px;
  font-weight: 900;
}

.tabs-row {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 28px;
  padding: 4px;
  border-radius: 16px;
  background: #e9ebf0;
}

.tabs-row button {
  min-height: 38px;
  padding: 0 14px;
  border: 0;
  border-radius: 13px;
  background: transparent;
  color: #18181b;
  cursor: pointer;
  font-size: 16px;
  font-weight: 900;
}

.tabs-row button.active {
  background: #fff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.tabs-row button.disabled {
  color: #a1a1aa;
  cursor: not-allowed;
}

.content-card {
  padding: 30px 32px;
}

.card-heading {
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.card-heading h2 {
  font-size: 25px;
  font-weight: 900;
}

.link-action {
  color: #0f82c3 !important;
  font-family: "montserrat-semibold";
  text-transform: none;
}

.crm-toolbar {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 190px 170px;
  gap: 12px;
  margin-bottom: 18px;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #fff;
}

.search-field input,
.crm-toolbar select {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
}

.crm-toolbar select {
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #fff;
}

.collections-table {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none !important;
}

.collections-table ::v-deep th {
  background: #f8fafc;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  text-transform: uppercase;
}

.collections-table ::v-deep td {
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.collections-table ::v-deep tbody tr {
  cursor: pointer;
}

.collections-table ::v-deep tbody tr:hover {
  background: #f8fbff !important;
}

.table-main-cell strong,
.calendar-card strong {
  display: block;
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
}

.table-main-cell small,
.calendar-card small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.status-pill,
.risk-tag {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 11px;
  border-radius: 999px;
  font-size: 13px;
  font-style: normal;
  font-weight: 900;
}

.risk-tag.low,
.status-pill.neutral {
  background: #f1f5f9;
  color: #334155;
}

.risk-tag.medium,
.status-pill.progress {
  background: #eef2ff;
  color: #3730a3;
}

.risk-tag.high,
.status-pill.warning {
  background: #fff1f2;
  color: #be123c;
}

.status-pill.success {
  background: #dcfce7;
  color: #15803d;
}

.invoice-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.invoice-summary button {
  min-width: 128px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.invoice-summary button.active {
  border-color: #93c5fd;
  background: #eff6ff;
}

.invoice-summary strong,
.invoice-summary span {
  display: block;
}

.invoice-summary strong {
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.invoice-summary span {
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.view-switch {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 14px;
  background: #e9ebf0;
}

.view-switch button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  font-weight: 900;
}

.view-switch button.active {
  background: #fff;
  color: #0f82c3;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.month-calendar {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #fff;
}

.month-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 18px;
  border-bottom: 1px solid #e5e7eb;
}

.month-calendar-header strong {
  color: #0f172a;
  font-size: 20px;
  font-weight: 900;
}

.month-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
}

.weekdays-row,
.month-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.weekdays-row {
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.weekdays-row span {
  padding: 12px 10px;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
}

.month-day {
  min-height: 146px;
  padding: 10px;
  border-right: 1px solid #eef2f7;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
}

.month-day.clickable {
  cursor: pointer;
}

.month-day.clickable:hover {
  background: #f8fbff;
}

.month-day:nth-child(7n) {
  border-right: 0;
}

.month-day.muted {
  background: #f8fafc;
}

.month-day.today {
  background: #eff6ff;
  box-shadow: inset 0 0 0 2px #93c5fd;
}

.month-day header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.month-day header span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.month-day.today header span {
  background: #0f82c3;
  color: #fff;
}

.month-day header strong {
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0f82c3;
  font-size: 12px;
  font-weight: 900;
  line-height: 22px;
  text-align: center;
}

.calendar-event {
  display: grid;
  width: 100%;
  gap: 2px;
  margin-bottom: 6px;
  padding: 7px 8px;
  border: 0;
  border-left: 3px solid #0f82c3;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
}

.calendar-event span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 900;
}

.calendar-event small {
  overflow: hidden;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-event.overdue {
  border-left-color: #dc2626;
  background: #fff1f2;
}

.calendar-event.today {
  border-left-color: #0f82c3;
  background: #e0f2fe;
}

.calendar-event.week {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.calendar-event.soon {
  border-left-color: #6366f1;
  background: #eef2ff;
}

.month-day em {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
}

.calendar-board {
  display: grid;
  grid-template-columns: repeat(4, minmax(220px, 1fr));
  gap: 14px;
}

.agenda-board {
  max-height: 680px;
  overflow-y: auto;
  padding-right: 4px;
}

.calendar-column {
  min-height: 420px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #f8fafc;
}

.calendar-column header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.calendar-column header strong {
  min-width: 26px;
  min-height: 26px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0f82c3;
  text-align: center;
  line-height: 26px;
}

.calendar-card {
  display: grid;
  gap: 7px;
  width: 100%;
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.calendar-card span {
  color: #475569;
  font-size: 13px;
  font-weight: 800;
}

.empty-state {
  padding: 20px;
  color: #64748b;
  font-weight: 800;
  text-align: center;
}

.pagination-hint {
  margin: 0 0 12px;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.workflow-list,
.timeline-list {
  display: grid;
  gap: 14px;
}

.workflow-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
}

.workflow-row h3 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 900;
}

.workflow-row p {
  margin-top: 10px;
  color: #475569;
  font-size: 16px;
  font-weight: 700;
}

.workflow-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.workflow-channels span {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 900;
}

.timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 14px;
  padding-bottom: 18px;
}

.timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  top: 18px;
  bottom: -4px;
  left: 8px;
  width: 2px;
  background: #dbeafe;
}

.timeline-dot {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 4px solid #dbeafe;
  border-radius: 50%;
  background: #0f82c3;
}

.timeline-item strong {
  color: #0f172a;
  font-size: 17px;
}

.timeline-item p {
  margin: 5px 0;
  color: #475569;
  font-weight: 700;
}

.timeline-item small {
  color: #94a3b8;
  font-weight: 800;
}

.template-grid,
.upload-grid {
  display: grid;
  gap: 20px;
}

.template-grid {
  grid-template-columns: 1fr 1fr;
}

.template-card,
.mapping-preview {
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
}

.template-card h3,
.mapping-preview h3 {
  margin-bottom: 16px;
  font-size: 19px;
  font-weight: 900;
}

.columns-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.columns-grid span {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.template-card ul {
  display: grid;
  gap: 12px;
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-weight: 700;
}

.upload-dialog h2 {
  font-size: 25px;
  font-weight: 900;
}

.upload-dialog .v-card__title {
  padding: 26px 28px 8px;
}

.upload-dialog .v-card__title p {
  margin-top: 8px;
  color: #64748b;
  font-size: 15px;
  font-weight: 700;
}

.upload-dialog .v-card__text {
  padding: 22px 28px;
}

.upload-grid {
  grid-template-columns: minmax(0, 1fr) 330px;
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  gap: 10px;
  border: 2px dashed #bfdbfe;
  border-radius: 16px;
  background: #f8fbff;
  cursor: pointer;
}

.dropzone strong {
  color: #0f172a;
  font-size: 18px;
}

.dropzone span {
  color: #64748b;
  font-weight: 700;
}

.hidden-input {
  display: none;
}

.upload-options {
  display: grid;
  gap: 14px;
}

.upload-options label {
  display: grid;
  gap: 7px;
}

.upload-options label span {
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.upload-options select,
.drawer-invoice-row select {
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  color: #0f172a;
  font-weight: 800;
}

.checkbox-row {
  display: flex !important;
  grid-template-columns: none;
  align-items: center;
  gap: 10px !important;
}

.mapping-preview {
  margin-top: 20px;
  background: #f8fafc;
}

.preview-table {
  display: grid;
  gap: 8px;
}

.preview-row {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 0.8fr 0.9fr 0.8fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}

.debtor-drawer {
  z-index: 1000;
}

.drawer-content {
  display: grid;
  gap: 16px;
  min-height: 100%;
  padding: 24px;
  background: #f8fafc;
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.drawer-header h2 {
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
}

.drawer-header p {
  margin-top: 6px;
  color: #64748b;
  font-size: 15px;
  font-weight: 800;
}

.drawer-header button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}

.drawer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.drawer-tags > span:not(.risk-tag) {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 900;
}

.drawer-panel {
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.drawer-panel h3 {
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 19px;
  font-weight: 900;
}

.aging-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.aging-grid article {
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
}

.aging-grid span,
.aging-grid strong {
  display: block;
}

.aging-grid span {
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.aging-grid strong {
  margin-top: 8px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
}

.drawer-invoice-table {
  display: grid;
  gap: 8px;
}

.drawer-invoice-row {
  display: grid;
  grid-template-columns: 0.85fr 0.8fr 0.9fr 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}

.drawer-invoice-row.header {
  background: #f1f5f9;
  color: #475569;
  text-transform: uppercase;
}

.chat-panel {
  padding: 14px;
}

.chat-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.chat-heading p {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.whatsapp-chat {
  display: flex;
  flex-direction: column;
  min-height: 460px;
  padding: 16px;
  border-radius: 18px;
  background:
    linear-gradient(rgba(226, 232, 240, 0.88), rgba(226, 232, 240, 0.88)),
    radial-gradient(circle at 15% 20%, rgba(15, 130, 195, 0.08), transparent 25%),
    #dfe5e8;
}

.chat-date {
  align-self: center;
  margin-bottom: 16px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  font-size: 13px;
  font-weight: 900;
}

.chat-message {
  display: flex;
  margin-bottom: 10px;
}

.chat-message.incoming {
  justify-content: flex-start;
}

.chat-message.outgoing {
  justify-content: flex-end;
}

.chat-message.system {
  justify-content: center;
  margin: 14px 0;
}

.chat-bubble {
  max-width: 78%;
  padding: 10px 12px 8px;
  border-radius: 12px;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.chat-message.incoming .chat-bubble {
  border-top-left-radius: 4px;
  background: #fff;
}

.chat-message.outgoing .chat-bubble {
  border-top-right-radius: 4px;
  background: #c9facc;
}

.chat-bubble strong {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.chat-bubble p {
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
}

.chat-bubble small {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.chat-system-line {
  display: grid;
  grid-template-columns: minmax(28px, 1fr) auto minmax(28px, 1fr);
  align-items: center;
  width: 100%;
  gap: 10px;
  color: #334155;
  font-size: 12px;
  font-weight: 900;
}

.chat-system-line span {
  height: 1px;
  background: rgba(100, 116, 139, 0.28);
}

.chat-system-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.chat-system-date {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
}

.chat-composer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 10px 10px 10px 14px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
}

.chat-composer span {
  flex: 1;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.chat-composer button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 13px;
  border: 0;
  border-radius: 10px;
  background: #1f2937;
  color: #fff;
  cursor: pointer;
  font-weight: 900;
}

.drawer-actions {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin: 0 -24px -24px;
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
  background: rgba(248, 250, 252, 0.94);
  backdrop-filter: blur(10px);
}

.day-detail-drawer {
  align-content: flex-start;
}

.day-invoice-list {
  display: grid;
  gap: 10px;
}

.day-invoice-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.day-invoice-card:hover {
  background: #f8fbff;
}

.day-invoice-card strong,
.day-invoice-card span,
.day-invoice-card small {
  display: block;
}

.day-invoice-card strong {
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
}

.day-invoice-card span {
  margin-top: 5px;
  color: #475569;
  font-size: 13px;
  font-weight: 900;
}

.day-invoice-card small {
  margin-top: 5px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
}

.day-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.day-summary-grid article {
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
}

.day-summary-grid span,
.day-summary-grid strong {
  display: block;
}

.day-summary-grid span {
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.day-summary-grid strong {
  margin-top: 6px;
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

@media (max-width: 1180px) {
  .metrics-grid,
  .template-grid,
  .upload-grid,
  .aging-grid {
    grid-template-columns: 1fr;
  }

  .crm-toolbar {
    grid-template-columns: 1fr 1fr;
  }

  .calendar-board,
  .month-calendar {
    overflow-x: auto;
  }

  .weekdays-row,
  .month-grid {
    min-width: 980px;
  }

  .calendar-board {
    grid-template-columns: repeat(4, minmax(240px, 1fr));
  }
}

@media (max-width: 760px) {
  .collections-agent {
    padding: 28px 18px 54px;
  }

  .collections-header,
  .card-heading,
  .workflow-row,
  .insight-banner {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions,
  .primary-action,
  .secondary-action,
  .details-button {
    width: 100%;
  }

  .metrics-grid,
  .crm-toolbar {
    grid-template-columns: 1fr;
  }

  .debtor-drawer {
    width: 100% !important;
  }

  .drawer-content {
    padding: 18px;
  }

  .drawer-invoice-row,
  .preview-row {
    grid-template-columns: 1fr;
  }

  .day-invoice-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .day-summary-grid {
    grid-template-columns: 1fr;
  }

  .drawer-actions {
    align-items: stretch;
    flex-direction: column;
    margin: 0 -18px -18px;
    padding: 14px 18px;
  }
}

/* --- Sincronización Nexor / bandeja de revisión --- */
.sync-control {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.sync-hint {
  color: #64748b;
  font-size: 11px;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 9px;
  background: #dc2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.agent-status-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 12px;
  font-style: normal;
}

.agent-status-empty {
  color: #cbd5e1;
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.review-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}

.review-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.review-main span {
  color: #475569;
  font-size: 13px;
}

.review-main small {
  color: #64748b;
  font-size: 12px;
}

.review-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.invoice-confirm-note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 2px 0 8px;
  color: #b45309;
  font-size: 12px;
}

.promise-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.promise-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.promise-item div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.promise-item span {
  color: #475569;
  font-size: 13px;
}

.promise-item small {
  color: #64748b;
  font-size: 12px;
}

@media (max-width: 720px) {
  .review-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
